/** Initial expenditure estimate from Mifflin-St Jeor and activity factors. */
import {
  ACTIVITY_MULTIPLIERS,
  ALGO_VERSIONS,
  DEFAULT_ACTIVITY_MULTIPLIER,
  STEPS_ADJUSTMENT_MAX,
  STEPS_MULTIPLIER_PER_1000,
  STEPS_REFERENCE,
} from "./constants";
import { calculateBmr } from "./metabolism";
import {
  ENGINE_VERSION,
  type EngineMeta,
  type EngineProfile,
  type EngineResult,
  type ExpenditureValue,
} from "./types";

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function createMeta(
  now: number,
  explanation: string,
  inputs: EngineMeta["inputs"],
): EngineMeta {
  return {
    kind: "initialTdee",
    engineVersion: ENGINE_VERSION,
    algorithmVersion: ALGO_VERSIONS.initialTdee,
    computedAt: now,
    estimateKind: "initial",
    confidence: "learning",
    explanation,
    inputs,
  };
}

export function calculateInitialTdee(
  profile: EngineProfile,
  now = Date.now(),
): EngineResult<ExpenditureValue> | null {
  const bmr = calculateBmr(profile, now);
  if (bmr === null) return null;

  const activityMultiplier =
    ACTIVITY_MULTIPLIERS[profile.activityLevel ?? ""] ?? DEFAULT_ACTIVITY_MULTIPLIER;
  const steps = profile.avgDailySteps;
  const stepsAdjustment =
    steps !== undefined && steps > 0
      ? clamp(
          ((steps - STEPS_REFERENCE) / 1000) * STEPS_MULTIPLIER_PER_1000,
          -STEPS_ADJUSTMENT_MAX,
          STEPS_ADJUSTMENT_MAX,
        )
      : 0;

  return {
    value: {
      kcalPerDay: Math.round(bmr.value.bmrKcal * (activityMultiplier + stepsAdjustment)),
      bmrKcal: bmr.value.bmrKcal,
      activityMultiplier,
      stepsAdjustment,
    },
    meta: createMeta(
      now,
      "This is your initial estimated expenditure from resting energy, activity, and known steps. It will be replaced by a personalised estimate as real food and weight data accumulate.",
      {
        bmrKcal: bmr.value.bmrKcal,
        activityLevel: profile.activityLevel ?? "unknown",
        activityMultiplier,
        avgDailySteps: steps !== undefined && steps > 0 ? Math.round(steps) : null,
        stepsAdjustment,
      },
    ),
  };
}
